import { useState } from "react";
import { ArrowRight, Building2, User, Users, HeartHandshake, Rocket } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "./Header";

const SIZES = [
  { id: "independant", label: "Indépendant", icon: User },
  { id: "tpe", label: "TPE (1 à 10)", icon: Building2 },
  { id: "pme", label: "PME (11 à 250)", icon: Users },
  { id: "startup", label: "Start-up", icon: Rocket },
  { id: "association", label: "Association", icon: HeartHandshake },
];

const NEEDS = [
  {
    id: "ponctuelle",
    label: "Mission ponctuelle",
    hint: "Contrat, revue juridique, appui ciblé",
    topic: "Droit des sociétés",
  },
  {
    id: "conformite",
    label: "Mise en conformité",
    hint: "RGPD, LCB-FT/KYC, procédures internes",
    topic: "Conformité bancaire & LCB-FT/KYC",
  },
  {
    id: "recurrent",
    label: "Accompagnement récurrent",
    hint: "DPO externalisé, veille et suivi réglementaire",
    topic: "Conformité numérique (RGPD, AI Act)",
  },
  {
    id: "formation",
    label: "Formation & atelier",
    hint: "Sensibilisation de vos équipes, sur-mesure",
    topic: "Formation & sensibilisation",
  },
];

export const Estimator = () => {
  const [size, setSize] = useState("tpe");
  const [need, setNeed] = useState("conformite");

  const sizeObj = SIZES.find((s) => s.id === size);
  const needObj = NEEDS.find((n) => n.id === need);

  const askQuote = (e) => {
    scrollToId(e, "#contact");
    window.dispatchEvent(
      new CustomEvent("alc:devis", {
        detail: { size: sizeObj.label, need: needObj.label, topic: needObj.topic },
      })
    );
  };

  const Option = ({ active, onClick, testid, children }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-pressed={active}
      className={`border px-4 py-3 text-left transition-all duration-300 ${
        active
          ? "border-gold bg-gold/10 text-ivory"
          : "border-ink-line bg-ink-card/40 text-mute hover:border-gold/50 hover:text-ivory"
      }`}
    >
      {children}
    </button>
  );

  return (
    <section
      id="estimation"
      data-testid="estimator-section"
      className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-36"
    >
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Devis personnalisé</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-ivory sm:text-4xl lg:text-5xl">
              Votre devis sur-mesure, en{" "}
              <em className="italic text-gold">deux clics</em>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-mute">
              Dites-nous qui vous êtes et ce dont vous avez besoin : votre
              demande de devis est pré-remplie, il ne reste qu'à l'envoyer.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.26em] text-mute">
              01 — Votre structure
            </p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {SIZES.map((s) => {
                const Icon = s.icon;
                return (
                  <Option
                    key={s.id}
                    active={size === s.id}
                    onClick={() => setSize(s.id)}
                    testid={`estimator-size-${s.id}`}
                  >
                    <span className="flex items-center gap-2.5 text-sm">
                      <Icon className="h-4 w-4 text-gold" strokeWidth={1.5} />
                      {s.label}
                    </span>
                  </Option>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.26em] text-mute">
              02 — Votre besoin
            </p>
            <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
              {NEEDS.map((n) => (
                <Option
                  key={n.id}
                  active={need === n.id}
                  onClick={() => setNeed(n.id)}
                  testid={`estimator-need-${n.id}`}
                >
                  <span className="block font-serif text-lg text-inherit">
                    {n.label}
                  </span>
                  <span className="mt-1 block text-xs leading-snug text-mute">
                    {n.hint}
                  </span>
                </Option>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div
              data-testid="estimator-result"
              className="relative overflow-hidden border border-gold/40 bg-ink-card p-8 sm:p-10"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-gold/10 blur-3xl"
              />
              <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-mute">
                Votre profil
              </p>
              <p className="mt-6 font-serif text-3xl font-medium leading-snug text-ivory sm:text-4xl">
                {sizeObj.label}
                <span className="text-gold"> · </span>
                <em className="italic text-gold">{needObj.label}</em>
              </p>
              <p className="mt-6 border-t border-ink-line pt-6 text-sm leading-relaxed text-mute">
                Chaque structure est unique : vos prestations sont proposées au
                forfait ou sur devis, établis sur mesure après un premier
                échange — sans engagement.
              </p>
              <button
                type="button"
                onClick={askQuote}
                data-testid="estimator-contact-cta"
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-gold px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ivory"
              >
                Demander mon devis personnalisé
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="mt-5 text-xs leading-relaxed text-mute/70">
                Le formulaire de contact sera pré-rempli avec votre sélection.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
